import * as Device from "expo-device";
import * as ExpoImagePicker from "expo-image-picker";
import { appVersion } from "@constants/Env";
import { resolveError } from "@lib/util/error";
import {
  safeNotify,
  isSupportedFile,
  supportMimeTypes,
  collectErrorDetails,
} from "./helpers";
import type { ImageFile } from "../ImagePicker";

export interface WebFilePickParams {
  files: FileList;
  allowsMultipleSelection: boolean;
  pickupMediaType: ExpoImagePicker.MediaTypeOptions;
  maxImageSize: number;
  maxVideoSize: number;
  resizeSize: number;
  onPickStart: () => void;
  onPickEnd: () => void;
  onChange: (files: ImageFile[] | ImageFile) => void;
  onError: (message: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

/**
 * Web環境でのファイル選択処理の中核ロジック
 * HTML5標準のinput要素から選択されたファイルを処理します
 */
export async function handleWebFilePickCore(
  params: WebFilePickParams
): Promise<void> {
  const {
    files,
    allowsMultipleSelection,
    pickupMediaType,
    maxImageSize,
    maxVideoSize,
    resizeSize,
    onPickStart,
    onPickEnd,
    onChange,
    onError,
    fileInputRef,
  } = params;

  try {
    onPickStart();
    const fileArray = Array.from(files);
    const imageFileList: ImageFile[] = [];

    // ファイルごとに処理
    for (let index = 0; index < fileArray.length; index += 1) {
      const file = fileArray[index];
      const fileSizeMB = file.size / (1024 * 1024);
      // MIMEタイプで判定、空の場合は拡張子でフォールバック
      const isVideoFile =
        file.type.startsWith("video/") ||
        (file.type === "" && /\.(mp4|mov)$/i.test(file.name));

      // サイズチェック
      if (isVideoFile && fileSizeMB > maxVideoSize) {
        throw new Error(
          `${fileSizeMB.toFixed(
            1
          )}MBの動画が選択されました。${maxVideoSize}MB以下に圧縮して選択してください。`
        );
      }
      if (!isVideoFile && fileSizeMB > maxImageSize) {
        throw new Error(
          `${fileSizeMB.toFixed(
            1
          )}MBの写真が選択されました。${maxImageSize}MB以下に圧縮して選択してください。`
        );
      }

      // MIMEタイプチェック（ファイル拡張子のフォールバック付き）
      // NOTE: ブラウザによっては.MOVファイルに対して空のMIMEタイプを返す場合がある
      if (!isSupportedFile(file.type, file.name)) {
        // デバッグ用ログ
        safeNotify(
          {
            type: "ImagePickerError: UnsupportedFileType",
            searchableType: "IMAGE_UPLOAD_ERROR_UnsupportedFileType",
            context: {
              platform: "web",
              fileInfo: {
                name: file.name,
                type: file.type,
                size: file.size,
                sizeMB: Number((file.size / (1024 * 1024)).toFixed(2)),
              },
              validation: {
                pickupMediaType,
                supportedTypes: supportMimeTypes(pickupMediaType),
              },
            },
            timestamp: new Date().toISOString(),
          },
          { level: "warning" }
        );
        throw new Error(
          `選択可能なファイルは${supportMimeTypes(pickupMediaType).join(
            ", "
          )}のいずれかです。`
        );
      }

      // 動画の場合はそのまま、画像の場合はリサイズ
      if (isVideoFile) {
        try {
          const uri = URL.createObjectURL(file);
          // Blob URL生成の検証
          if (uri.length === 0) {
            throw new Error("Failed to create Blob URL for video");
          }
          imageFileList.push({ uri, file });
        } catch (videoError) {
          // 動画のBlob URL生成失敗のログ
          safeNotify(
            {
              type: "ImagePickerError: VideoProcessFailed",
              searchableType: "VIDEO_PROCESS_FAILED_Web",
              context: {
                platform: "web",
                error: String(videoError),
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                fileSizeMB: Number((file.size / (1024 * 1024)).toFixed(2)),
                deviceInfo: {
                  appVersion,
                  osVersion: Device.osVersion,
                  modelName: Device.modelName,
                },
              },
            },
            { level: "error" }
          );
          throw new Error(`動画ファイルの処理に失敗しました: ${file.name}`);
        }
      } else {
        // 画像をリサイズ
        const uri = URL.createObjectURL(file);

        try {
          // eslint-disable-next-line no-await-in-loop
          const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = (error) => {
              reject(
                new Error(
                  `Image load failed: ${
                    error instanceof Event ? error.type : String(error)
                  }`
                )
              );
            };
            image.src = uri;
          });

          const shouldResize =
            img.width > resizeSize || img.height > resizeSize;
          if (shouldResize) {
            // Canvas APIを使用してリサイズを試みる
            try {
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");
              if (ctx === null) {
                throw new Error("Canvas context not available");
              }

              const scale =
                img.width > img.height
                  ? resizeSize / img.width
                  : resizeSize / img.height;
              canvas.width = img.width * scale;
              canvas.height = img.height * scale;
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

              // eslint-disable-next-line no-await-in-loop
              const blob = await new Promise<Blob>((resolve, reject) => {
                // toBlobが使えない場合のフォールバック
                if (typeof canvas.toBlob !== "function") {
                  try {
                    // toDataURLを使用してBlobに変換
                    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
                    const binaryString = atob(dataUrl.split(",")[1]);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i += 1) {
                      bytes[i] = binaryString.charCodeAt(i);
                    }
                    resolve(new Blob([bytes], { type: "image/jpeg" }));
                  } catch (fallbackError) {
                    reject(
                      new Error(
                        `toBlob and fallback failed: ${String(fallbackError)}`
                      )
                    );
                  }
                  return;
                }

                canvas.toBlob(
                  (b) => {
                    if (b !== null) {
                      resolve(b);
                    } else {
                      reject(new Error("toBlob returned null"));
                    }
                  },
                  "image/jpeg",
                  0.9
                );
              });

              // File()コンストラクタのフォールバック
              let resizedFile: File;
              try {
                resizedFile = new File([blob], file.name, {
                  type: "image/jpeg",
                });
              } catch (fileError) {
                // File()が使えない場合はBlobをFileとして扱う
                const fileBlob: File = Object.assign(blob, {
                  name: file.name,
                  lastModified: Date.now(),
                }) as File;
                resizedFile = fileBlob;
              }

              const resizedUri = URL.createObjectURL(resizedFile);
              imageFileList.push({ uri: resizedUri, file: resizedFile });
              // 元のURIを解放
              URL.revokeObjectURL(uri);
            } catch (resizeError) {
              // Canvas APIでリサイズ失敗した場合は元の画像を使用
              safeNotify(
                {
                  type: "ImagePickerWarning: ResizeFailed",
                  searchableType: "IMAGE_RESIZE_FAILED_Web",
                  context: {
                    platform: "web",
                    error: String(resizeError),
                    fileName: file.name,
                    fileSize: file.size,
                    imageWidth: img.width,
                    imageHeight: img.height,
                    fallbackAction: "using_original_image",
                  },
                },
                { level: "warning" }
              );
              // リサイズ失敗時は元の画像を使用
              imageFileList.push({ uri, file });
            }
          } else {
            imageFileList.push({ uri, file });
          }
        } catch (imageLoadError) {
          // 画像読み込み失敗のログ
          safeNotify(
            {
              type: "ImagePickerError: ImageLoadFailed",
              searchableType: "IMAGE_LOAD_FAILED_Web",
              context: {
                platform: "web",
                error: String(imageLoadError),
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
              },
            },
            { level: "error" }
          );
          // 画像読み込み失敗時もBlobURLを解放
          URL.revokeObjectURL(uri);
          throw imageLoadError;
        }
      }
    }

    // 成功ログ
    safeNotify(
      {
        type: "ImagePickerSuccess: WebFilePick",
        searchableType: "IMAGE_UPLOAD_SUCCESS_Web",
        context: {
          platform: "web",
          filesInfo: {
            totalFiles: imageFileList.length,
            files: imageFileList.map((item, idx) => ({
              index: idx,
              size: item.file.size,
              sizeMB: Number((item.file.size / (1024 * 1024)).toFixed(2)),
              type: item.file.type,
              name: item.file.name,
            })),
          },
          deviceInfo: {
            appVersion,
            osVersion: Device.osVersion,
            modelName: Device.modelName,
          },
        },
        timestamp: new Date().toISOString(),
      },
      { level: "info" }
    );

    if (allowsMultipleSelection) {
      onChange(imageFileList);
    } else if (imageFileList.length > 0) {
      onChange(imageFileList[0]);
    }
  } catch (e: unknown) {
    try {
      const errorDetails = collectErrorDetails(e, {
        operation: "WebFilePick",
        additionalInfo: {
          pickupMediaType,
          maxImageSize,
          maxVideoSize,
          allowsMultipleSelection,
        },
      });

      safeNotify({
        type: "ImagePickerError: WebFilePick",
        searchableType: "IMAGE_UPLOAD_ERROR_WebFilePick",
        ...errorDetails,
      });
    } catch (notificationError) {
      // eslint-disable-next-line no-console
      console.warn("WebFilePick notification failed:", notificationError);
    }

    onError(resolveError(e).message);
  } finally {
    onPickEnd();
    // input要素をリセット（同じファイルを再選択可能にする）
    if (fileInputRef.current !== null) {
      fileInputRef.current.value = "";
    }
  }
}
